import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, buildApiUrl, useDjangoAPI } from '../config/api';

/**
 * Service zum Laden und Verarbeiten von Anfragedaten
 * Unterstützt sowohl Express.js als auch Django Backend
 */
class RequestService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    console.log('RequestService initialisiert mit:', {
      baseUrl: this.baseUrl,
      useDjangoAPI: useDjangoAPI,
      backend: useDjangoAPI ? 'Django' : 'Express'
    });
  }

  /**
   * Hilfsmethode zum Laden der Beispieldaten, wenn das Backend nicht erreichbar ist
   * @returns {Object} Beispieldaten
   */
  getFallbackData() {
    return {
      requests: [
        { 
          id: 1, 
          title: "Diabetes Typ 2 Studie", 
          company: "Pharma AG",
          date: "08.03.2025",
          status: "new",
          patientCount: null,
          price: null,
          description: "Analyse von Risikofaktoren bei Diabetes-Typ-2 Patienten mit Fokus auf kardiovaskuläre Komplikationen."
        },
        { 
          id: 2, 
          title: "Kardiovaskuläre Risikofaktoren", 
          company: "MedResearch GmbH",
          date: "01.03.2025",
          status: "feasibility",
          patientCount: 178,
          price: null,
          description: "Untersuchung der Korrelation zwischen Bluthochdruck und Herzinsuffizienz bei Patienten über 60 Jahren."
        },
        { 
          id: 3, 
          title: "Onkologische Biomarker", 
          company: "BioPharm Inc.",
          date: "20.02.2025",
          status: "negotiation",
          patientCount: 89,
          price: 4500,
          description: "Identifikation von Biomarkern für die Früherkennung von Darmkrebs bei Risikopatienten."
        }
      ],
      recentRequests: [
        { id: 1, title: "Diabetes Typ 2 Studie", status: "new", date: "08.03.2025", company: "Pharma AG" },
        { id: 2, title: "Kardiovaskuläre Risikofaktoren", status: "feasibility", date: "01.03.2025", company: "MedResearch GmbH" },
        { id: 3, title: "Onkologische Biomarker", status: "negotiation", date: "20.02.2025", company: "BioPharm Inc." }
      ],
      stats: {
        totalRequests: 12,
        pendingRequests: 5,
        completedRequests: 7,
        revenue: 15000
      },
      chartData: [
        { name: "Jan", anfragen: 4, abgeschlossen: 3 },
        { name: "Feb", anfragen: 7, abgeschlossen: 5 },
        { name: "Mär", anfragen: 12, abgeschlossen: 7 }
      ]
    };
  }

  /**
   * Führt einen API-Aufruf durch
   * @param {string} endpoint - Der API-Endpunkt (kann relativer Pfad oder vollständige URL sein)
   * @param {string} method - HTTP-Methode (default: 'GET')
   * @param {Object} data - Request-Daten für POST/PUT
   * @returns {Promise<any>} Die API-Antwort
   */
  async apiCall(endpoint, method = 'GET', data = null) {
    try {
      // Build full URL if endpoint is relative
      const url = endpoint.startsWith('http') ? endpoint : buildApiUrl(endpoint);
      console.log(`API-Aufruf (${method}) an ${url}`);

      const config = {
        method,
        url,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json'
        }
      };

      // Add authentication token if available
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add data for POST/PUT requests
      if (data && (method === 'POST' || method === 'PUT')) {
        config.data = data;
      }

      const response = await axios(config);
      console.log(`API-Antwort von ${endpoint}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim API-Aufruf ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Gibt alle Anfragen zurück
   * @returns {Promise<Array>} Promise mit Liste aller Anfragen
   */
  async getAllRequests() {
    try {
      let endpoint;

      if (useDjangoAPI) {
        // Django Provider Inbox API - Use PROVIDER_REQUESTS for provider requests list
        endpoint = API_ENDPOINTS.PROVIDER_REQUESTS.LIST; // '/provider/inbox/'
      } else {
        // Express API
        endpoint = '/requests';
      }

      const response = await this.apiCall(endpoint);
      console.log("Geladene Anfragen:", response);

      // Handle Django response format
      if (useDjangoAPI) {
        // Django returns paginated results with 'results' array or direct array
        if (response.results && Array.isArray(response.results)) {
          // Paginated response
          return response.results;
        } else if (Array.isArray(response)) {
          // Direct array response
          return response;
        } else {
          console.error("Unexpected Django API response format:", response);
          return [];
        }
      } else {
        // Express API returns direct array
        return Array.isArray(response) ? response : [];
      }
    } catch (error) {
      console.error("Fehler beim Laden aller Anfragen:", error);
      console.log("Verwende Fallback-Daten für Anfragen");

      // Versuche, die Daten direkt aus der JSON-Datei zu laden (nur für Express fallback)
      if (!useDjangoAPI) {
        try {
          const response = await axios.get('/backend_temp/data/requestsData.json');
          if (response.data && Array.isArray(response.data.requests)) {
            console.log("Daten direkt aus JSON geladen:", response.data.requests);
            return response.data.requests;
          }
        } catch (jsonError) {
          console.error("Fehler beim direkten Laden der JSON-Datei:", jsonError);
        }
      }

      return this.getFallbackData().requests;
    }
  }

  /**
   * Gibt eine Anfrage anhand ihrer ID zurück
   * @param {number|string} id - Die ID der Anfrage
   * @returns {Promise<Object|null>} Promise mit der Anfrage oder null, wenn nicht gefunden
   */
  async getRequestById(id) {
    if (!id) {
      console.error("Keine ID übergeben");
      return null;
    }
    
    try {
      console.log(`RequestService: Lade Anfrage mit ID ${id}`);

      let request;
      if (useDjangoAPI) {
        // For Django: Use detailed inbox endpoint or search in all requests
        try {
          // Try to get from detailed endpoint if it exists - Use PROVIDER_REQUESTS for provider requests
          request = await this.apiCall(`${API_ENDPOINTS.PROVIDER_REQUESTS.DETAIL}`.replace('{id}', id));
        } catch (detailError) {
          // Fallback: Search in all requests
          const allRequests = await this.getAllRequests();
          request = allRequests.find(r => r.id === parseInt(id));
        }
      } else {
        // For Express: Use regular endpoint
        request = await this.apiCall(`/requests/${id}`);
      }

      console.log(`RequestService: Anfrage geladen:`, request);
      
      // Stelle sicher, dass die benötigten Eigenschaften existieren
      if (request) {
        if (!request.cohortDefinition) {
          request.cohortDefinition = [];
        }
        
        if (!request.requestedVariables) {
          request.requestedVariables = [];
        }
        
        if (!request.variableAvailability) {
          request.variableAvailability = {};
        }
        
        if (!request.feasibilityProgress && request.feasibilityProgress !== 0) {
          request.feasibilityProgress = request.status === 'feasibility' ? 60 : 0;
        }
      }
      
      return request;
    } catch (error) {
      console.error(`Fehler beim Laden der Anfrage mit ID ${id}:`, error);
      console.log("Versuche, alle Anfragen zu laden und die mit der ID zu finden");
      
      try {
        const allRequests = await this.getAllRequests();
        const foundRequest = allRequests.find(r => r.id === parseInt(id));
        
        if (foundRequest) {
          console.log(`Anfrage mit ID ${id} in allen Anfragen gefunden:`, foundRequest);
          
          // Stelle sicher, dass die benötigten Eigenschaften existieren
          if (!foundRequest.cohortDefinition) {
            foundRequest.cohortDefinition = [];
          }
          
          if (!foundRequest.requestedVariables) {
            foundRequest.requestedVariables = [];
          }
          
          if (!foundRequest.variableAvailability) {
            foundRequest.variableAvailability = {};
          }
          
          if (!foundRequest.feasibilityProgress && foundRequest.feasibilityProgress !== 0) {
            foundRequest.feasibilityProgress = foundRequest.status === 'feasibility' ? 60 : 0;
          }
          
          return foundRequest;
        }
      } catch (innerError) {
        console.error("Fehler beim Laden aller Anfragen:", innerError);
      }
      
      console.log("Verwende Fallback-Daten für Anfrage mit ID", id);
      const fallbackRequest = this.getFallbackData().requests.find(r => r.id === parseInt(id));
      
      // Stelle sicher, dass die benötigten Eigenschaften existieren
      if (fallbackRequest) {
        if (!fallbackRequest.cohortDefinition) {
          fallbackRequest.cohortDefinition = [];
        }
        
        if (!fallbackRequest.requestedVariables) {
          fallbackRequest.requestedVariables = [];
        }
        
        if (!fallbackRequest.variableAvailability) {
          fallbackRequest.variableAvailability = {};
        }
        
        if (!fallbackRequest.feasibilityProgress && fallbackRequest.feasibilityProgress !== 0) {
          fallbackRequest.feasibilityProgress = fallbackRequest.status === 'feasibility' ? 60 : 0;
        }
      }
      
      return fallbackRequest || null;
    }
  }

  /**
   * Gibt die neuesten Anfragen zurück
   * @param {boolean} isProviderDashboard - True wenn Provider Dashboard, false für Client Dashboard
   * @returns {Promise<Array>} Promise mit Liste der neuesten Anfragen
   */
  async getRecentRequests(isProviderDashboard = false) {
    try {
      if (useDjangoAPI) {
        // For Django: Use appropriate endpoint based on context
        if (isProviderDashboard) {
          return await this.apiCall(API_ENDPOINTS.PROVIDER_DASHBOARD.RECENT);
        } else {
          return await this.apiCall(API_ENDPOINTS.REQUESTS.RECENT);
        }
      } else {
        return await this.apiCall('/recent-requests');
      }
    } catch (error) {
      console.error("Fehler beim Laden der neuesten Anfragen:", error);
      console.log("Verwende Fallback-Daten für neueste Anfragen");
      return this.getFallbackData().recentRequests;
    }
  }

  /**
   * Gibt die Statistiken zurück
   * @param {boolean} isProviderDashboard - True wenn Provider Dashboard, false für Client Dashboard
   * @returns {Promise<Object>} Promise mit Statistiken
   */
  async getStats(isProviderDashboard = false) {
    try {
      if (useDjangoAPI) {
        // For Django: Use appropriate endpoint based on context
        if (isProviderDashboard) {
          const stats = await this.apiCall(API_ENDPOINTS.PROVIDER_DASHBOARD.STATS);
          return stats;
        } else {
          const stats = await this.apiCall(API_ENDPOINTS.REQUESTS.STATS);
          return stats;
        }
      } else {
        return await this.apiCall('/stats');
      }
    } catch (error) {
      console.error("Fehler beim Laden der Statistiken:", error);
      console.log("Verwende Fallback-Daten für Statistiken");
      return this.getFallbackData().stats;
    }
  }

  /**
   * Gibt die Chartdaten zurück
   * @param {boolean} isProviderDashboard - True wenn Provider Dashboard, false für Client Dashboard
   * @returns {Promise<Array>} Promise mit Chartdaten
   */
  async getChartData(isProviderDashboard = false) {
    try {
      if (useDjangoAPI) {
        // For Django: Use appropriate endpoint based on context
        if (isProviderDashboard) {
          return await this.apiCall(API_ENDPOINTS.PROVIDER_DASHBOARD.CHARTS);
        } else {
          return await this.apiCall(API_ENDPOINTS.REQUESTS.CHART_DATA);
        }
      } else {
        return await this.apiCall('/chart-data');
      }
    } catch (error) {
      console.error("Fehler beim Laden der Chartdaten:", error);
      console.log("Verwende Fallback-Daten für Chartdaten");
      return this.getFallbackData().chartData;
    }
  }
  
  /**
   * Lädt Dashboard-Daten (kombinierte Daten für die Übersicht)
   * @returns {Promise<Object>} Dashboard-Daten
   */
  async getDashboardData() {
    try {
      return await this.apiCall('/dashboard');
    } catch (error) {
      console.error("Fehler beim Laden der Dashboard-Daten:", error);
      console.log("Verwende Fallback-Daten für Dashboard");
      
      const fallbackData = this.getFallbackData();
      return {
        recentStudies: fallbackData.recentRequests,
        stats: fallbackData.stats,
        chartData: fallbackData.chartData
      };
    }
  }

  /**
   * Aktualisiert eine Anfrage
   * @param {number} id - Die ID der Anfrage
   * @param {Object} data - Die aktualisierten Daten
   * @returns {Promise<Object>} Promise mit der aktualisierten Anfrage
   */
  /**
   * Lädt die Machbarkeitsdaten für eine bestimmte Anfrage
   * @param {number|string} id - Die ID der Anfrage
   * @returns {Promise<Object|null>} Promise mit den Machbarkeitsdaten oder null, wenn nicht gefunden
   */
  async getFeasibilityCheckData(id) {
    if (!id) {
      console.error("Keine ID übergeben für Machbarkeitsprüfung");
      return null;
    }
    
    // Stelle sicher, dass die ID eine Zahl ist
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      console.error(`Ungültige ID für Machbarkeitsprüfung: ${id}`);
      return null;
    }
    
    try {
      console.log(`RequestService: Lade Machbarkeitsdaten für Anfrage mit ID ${numericId}`);

      let request;
      if (useDjangoAPI) {
        // For Django: Use regular inbox endpoint to get request details
        request = await this.getRequestById(numericId);
      } else {
        // For Express: Use dedicated feasibility endpoint
        request = await this.apiCall(`/requests/${numericId}/feasibility-check`);
      }

      console.log(`RequestService: Machbarkeitsdaten geladen:`, request);

      // Überprüfe, ob die ID in der Antwort mit der angeforderten ID übereinstimmt
      if (request && request.id !== numericId) {
        console.error(`ID-Mismatch in getFeasibilityCheckData: Angefordert ${numericId}, erhalten ${request.id}`);
      }

      // Zusätzliche Prüfung, ob die Antwort gültig ist
      if (!request || typeof request !== 'object') {
        console.error(`Ungültige Antwort vom Server für ID ${numericId}:`, request);
        return null;
      }
      
      // Stelle sicher, dass die benötigten Eigenschaften existieren
      if (request) {
        if (!request.cohortDefinition) {
          request.cohortDefinition = [];
        }
        
        if (!request.requestedVariables) {
          request.requestedVariables = [];
        }
        
        if (!request.variableAvailability) {
          request.variableAvailability = {};
        }
        
        if (!request.feasibilityProgress && request.feasibilityProgress !== 0) {
          request.feasibilityProgress = request.status === 'feasibility' ? 60 : 0;
        }
      }
      
      return request;
    } catch (error) {
      console.error(`Fehler beim Laden der Machbarkeitsdaten für ID ${id}:`, error);
      
      // Versuche, die Daten direkt aus der Anfrage zu laden
      try {
        console.log(`Versuche, reguläre Anfragedaten für ID ${id} zu laden`);
        return await this.getRequestById(id);
      } catch (fallbackError) {
        console.error(`Auch Fallback fehlgeschlagen:`, fallbackError);
        throw error;
      }
    }
  }

  /**
   * Extrahiert die Request-ID aus einer URL
   * @param {string} url - Die URL, aus der die ID extrahiert werden soll
   * @returns {string|null} Die extrahierte ID oder null, wenn keine gefunden wurde
   */
  extractRequestIdFromUrl(url) {
    if (!url) return null;
    
    // Versuche, die ID aus einem URL-Pfad wie "/requests/123" zu extrahieren
    const matches = url.match(/\/requests\/(\d+)/);
    if (matches && matches[1]) {
      console.log(`ID ${matches[1]} aus URL ${url} extrahiert`);
      return matches[1];
    }
    
    // Versuche, die ID aus einem URL-Pfad wie "/feasibility-check/123" zu extrahieren
    const feasibilityMatches = url.match(/\/feasibility-check\/(\d+)/);
    if (feasibilityMatches && feasibilityMatches[1]) {
      console.log(`ID ${feasibilityMatches[1]} aus URL ${url} extrahiert`);
      return feasibilityMatches[1];
    }
    
    // Versuche, die ID aus einem Query-Parameter wie "?id=123" zu extrahieren
    const queryMatch = url.match(/[?&]id=(\d+)/);
    if (queryMatch && queryMatch[1]) {
      console.log(`ID ${queryMatch[1]} aus Query-Parameter extrahiert`);
      return queryMatch[1];
    }
    
    console.log(`Keine ID in URL ${url} gefunden`);
    return null;
  }
  
  /**
   * Generiert den URL-Pfad für die Machbarkeitsprüfung einer Anfrage
   * @param {number|string} id - Die ID der Anfrage
   * @returns {string} Der URL-Pfad für die Machbarkeitsprüfung
   */
  getFeasibilityCheckUrl(id) {
    if (!id) {
      console.error("Keine ID für Machbarkeitsprüfung-URL übergeben");
      return '/feasibility-check';
    }
    
    return `/feasibility-check/${id}`;
  }

  async updateRequest(id, data) {
    try {
      console.log(`Aktualisiere Anfrage ${id} mit Daten:`, data);
      const response = await axios.put(`${this.baseUrl}/requests/${id}`, data);
      console.log('Aktualisierungsantwort:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren der Anfrage mit ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Testet die Verbindung zum Backend
   * @returns {Promise<Object>} Status der Verbindung
   */
  async testConnection() {
    try {
      let testUrl;
      if (useDjangoAPI) {
        // Für Django: Verwende den gemeinsamen Core-Endpunkt für System-Status
        testUrl = `${this.baseUrl}/core/status/`;
      } else {
        // Für Express: Verwende den ursprünglichen Test-Endpunkt
        testUrl = `${this.baseUrl}/test`;
      }

      console.log(`Teste Backend-Verbindung zu: ${testUrl}`);
      const response = await axios.get(testUrl, {
        timeout: 3000,
        headers: {
          // Token hinzufügen, falls vorhanden
          ...(localStorage.getItem('token') && {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          })
        }
      });

      return {
        connected: true,
        message: useDjangoAPI ?
          `${response.data.message} (${response.data.status})` :
          response.data.message,
        timestamp: response.data.timestamp || new Date().toISOString(),
        url: this.baseUrl,
        environment: response.data.environment,
        database: response.data.database
      };
    } catch (error) {
      console.error('Backend-Verbindungsfehler:', error);

      // Für Django: Ein 401/403 ist auch ein Zeichen, dass das Backend läuft
      if (useDjangoAPI && (error.response?.status === 401 || error.response?.status === 403)) {
        return {
          connected: true,
          message: 'Django Backend erreichbar (Authentifizierung erforderlich)',
          timestamp: new Date().toISOString(),
          url: this.baseUrl
        };
      }

      return {
        connected: false,
        error: error.message || 'Verbindung fehlgeschlagen'
      };
    }
  }

  /**
   * Erstellt eine neue Anfrage
   * @param {Object} data - Die Daten der neuen Anfrage
   * @returns {Promise<Object>} Promise mit der erstellten Anfrage
   */
  async createRequest(data) {
    try {
      console.log('Erstelle neue Anfrage mit Daten:', data);
      const response = await axios.post(`${this.baseUrl}/requests`, data);
      console.log('Antwort nach Erstellung:', response.data);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Erstellen der Anfrage:', error);
      throw error;
    }
  }
}

export default new RequestService();
